import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Flex, theme } from 'antd';

import { NexusButton } from './NexusButton';
import { useSidePane } from '../../../../../layout/SidePane';

const NEXUS_GUTTER_OFFSET = 80;
const NEXUS_PANE_WIDTH = 512;

export type NexusDockProps = {
  readonly children: ReactNode;
  readonly isOpen: boolean;
  readonly offersButton: boolean;
  readonly onOpen: () => void;
  readonly pane: ReactNode;
  /** A screen shorter than the page keeps the button beside its own bottom edge, not the page's. */
  readonly fitsContent?: boolean;
};

export const NexusDock = ({ children, isOpen, offersButton, onOpen, pane, fitsContent = false }: NexusDockProps) => {
  const { token } = theme.useToken();
  const sidePane = useSidePane();

  const paneColumn = (
    <Flex
      vertical
      style={{
        width: NEXUS_PANE_WIDTH,
        flexShrink: 0,
        backgroundColor: token.colorBgContainer,
        borderLeft: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {pane}
    </Flex>
  );

  return (
    <Flex flex={1} gap={token.marginLG} style={{ width: '100%' }}>
      <Flex
        vertical
        flex={1}
        style={{ minWidth: 0, position: 'relative', alignSelf: fitsContent ? 'flex-start' : 'stretch' }}
      >
        {children}

        {/* Closed, Nexus sits in the gutter: a column would misalign the step indicator with the content. */}
        {!isOpen && offersButton && (
          <div style={{ position: 'sticky', bottom: 0, height: 0 }}>
            <div
              style={{ position: 'absolute', left: '100%', bottom: token.marginLG, marginLeft: NEXUS_GUTTER_OFFSET }}
            >
              <NexusButton onClick={onOpen} />
            </div>
          </div>
        )}
      </Flex>

      {/* Beside the content, not floating: positioned absolutely it covered the text or fell off-screen. */}
      {isOpen && (sidePane ? createPortal(paneColumn, sidePane) : paneColumn)}
    </Flex>
  );
};
